// Admin API client for connecting to backend-admin (port 8788)

const API_BASE = import.meta.env.VITE_ADMIN_API_URL || 'http://localhost:8788';

export interface AdminUser {
  id: number;
  username: string;
  role: 'admin' | 'customer';
  companyId: number | null;
}

export interface Company {
  id: number;
  name: string;
  slug: string;
  created_at: string;
}

export interface SocialLinks {
  instagram?: string;
  whatsapp?: string;
  facebook?: string;
  googleReview?: string;
}

export interface CompanyMedia {
  url: string;
  type: 'image' | 'video';
  filename: string;
  uploadedAt: string;
}

export interface CompanyDetails {
  id: number;
  name: string;
  slug: string;
  logoUrl: string | null;
  socialLinks?: SocialLinks;
  media?: CompanyMedia[];
  createdAt: string;
  updatedAt: string;
  stats: {
    users: number;
    reviews: number;
    walls: number;
    devices: number;
    menus: number;
  };
}

export interface LoginResponse {
  token: string;
  user: AdminUser;
}

export interface ImportRequest {
  companyId: number;
  reviews: any[];
  format?: 'outscraper';
}

export interface ImportResponse {
  imported: number;
  duplicates: number;
  errors: number;
}

export interface SystemStats {
  totalUsers: number;
  totalCompanies: number;
  totalReviews: number;
  totalDevices: number;
}

export interface CompanyStats {
  companyId: number;
  companyName: string;
  reviewCount: number;
  deviceCount: number;
  wallCount: number;
  averageRating: number;
}

export interface Device {
  id: number;
  name: string;
  token: string;
  companyId: number;
  wallId: number | null;
  isActive: boolean;
  lastSeen: string | null;
  company?: { id: number; name: string };
  wall?: { id: number; name: string };
}

export interface Wall {
  id: number;
  name: string;
  template: string;
  companyId: number;
  isActive: boolean;
  config?: any;
}

export interface WallWithCompany {
  id: number;
  name: string;
  template: string;
  config?: any;
  companyId: number;
  companyName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface PairDeviceRequest {
  code: string;
  name: string;
  companyId: number;
  wallId?: number;
}

class AdminAPIClient {
  private getHeaders(): HeadersInit {
    const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE}${path}`;
    const headers = this.getHeaders();

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Authentication
  async login(username: string, password: string): Promise<LoginResponse> {
    return this.request<LoginResponse>('/api/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  // User Management
  async getUsers(): Promise<AdminUser[]> {
    return this.request<AdminUser[]>('/api/admin/users');
  }

  async createUser(username: string, password: string, role: 'admin' | 'customer', companyId?: number): Promise<AdminUser> {
    return this.request<AdminUser>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify({ username, password, role, companyId }),
    });
  }

  async updateUserPassword(userId: number, newPassword: string): Promise<void> {
    return this.request<void>(`/api/admin/users/${userId}/password`, {
      method: 'PATCH',
      body: JSON.stringify({ newPassword }),
    });
  }

  async deleteUser(userId: number): Promise<void> {
    return this.request<void>(`/api/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Company Management
  async getCompanies(): Promise<Company[]> {
    return this.request<Company[]>('/api/admin/companies');
  }

  async getCompanyDetails(companyId: number): Promise<CompanyDetails> {
    return this.request<CompanyDetails>(`/api/admin/companies/${companyId}`);
  }

  async createCompany(name: string, slug: string): Promise<Company> {
    return this.request<Company>('/api/admin/companies', {
      method: 'POST',
      body: JSON.stringify({ name, slug }),
    });
  }

  async updateCompany(companyId: number, data: { name?: string; slug?: string; logoUrl?: string; socialLinks?: SocialLinks }): Promise<Company> {
    return this.request<Company>(`/api/admin/companies/${companyId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteCompany(companyId: number): Promise<void> {
    return this.request<void>(`/api/admin/companies/${companyId}`, {
      method: 'DELETE',
    });
  }

  // Company Media
  async uploadCompanyMedia(companyId: number, file: File): Promise<CompanyMedia> {
    const formData = new FormData();
    formData.append('file', file);

    const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
    const response = await fetch(`${API_BASE}/api/admin/companies/${companyId}/media`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async deleteCompanyMedia(companyId: number, url: string): Promise<void> {
    return this.request<void>(`/api/admin/companies/${companyId}/media`, {
      method: 'DELETE',
      body: JSON.stringify({ url }),
    });
  }

  // Review Management
  async getCompanyReviews(companyId: number): Promise<any[]> {
    return this.request<any[]>(`/api/admin/companies/${companyId}/reviews`);
  }

  async updateReviewVisibility(
    reviewId: number,
    data: { isVisible?: boolean; showOnMenu?: boolean }
  ): Promise<any> {
    return this.request(`/api/admin/reviews/${reviewId}/visibility`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async updateReview(
    reviewId: number,
    data: {
      authorName?: string;
      authorImage?: string;
      rating?: number;
      text?: string;
      photos?: string[];
      isVisible?: boolean;
      showOnMenu?: boolean;
    }
  ): Promise<any> {
    return this.request(`/api/admin/reviews/${reviewId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Bulk Import
  async importReviews(data: ImportRequest): Promise<ImportResponse> {
    return this.request<ImportResponse>('/api/admin/import/reviews', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Analytics
  async getSystemStats(): Promise<SystemStats> {
    return this.request<SystemStats>('/api/admin/analytics/system');
  }

  async getCompanyStats(): Promise<CompanyStats[]> {
    return this.request<CompanyStats[]>('/api/admin/analytics/companies');
  }

  async getCompanySingleStats(companyId: number): Promise<CompanyStats> {
    return this.request<CompanyStats>(`/api/admin/analytics/companies/${companyId}`);
  }

  // Device Management
  async getDevices(): Promise<Device[]> {
    return this.request<Device[]>('/api/admin/devices');
  }

  async getWallsByCompany(companyId: number): Promise<Wall[]> {
    return this.request<Wall[]>(`/api/admin/walls?companyId=${companyId}`);
  }

  async pairDevice(data: PairDeviceRequest): Promise<Device> {
    return this.request<Device>('/api/device/pairing/verify', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Wall Management
  async getAllWalls(companyId?: number): Promise<WallWithCompany[]> {
    const url = companyId ? `/api/admin/walls?companyId=${companyId}` : '/api/admin/walls';
    return this.request<WallWithCompany[]>(url);
  }

  async getWall(wallId: number): Promise<WallWithCompany> {
    return this.request<WallWithCompany>(`/api/admin/walls/${wallId}`);
  }

  async createWall(data: {
    companyId: number;
    name: string;
    template: string;
    config?: any;
    isActive?: boolean;
  }): Promise<Wall> {
    return this.request<Wall>('/api/admin/walls', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWall(wallId: number, data: {
    name?: string;
    template?: string;
    config?: any;
    isActive?: boolean;
  }): Promise<Wall> {
    return this.request<Wall>(`/api/admin/walls/${wallId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteWall(wallId: number): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/api/admin/walls/${wallId}`, {
      method: 'DELETE',
    });
  }

  // ==================== MENU MANAGEMENT ====================

  async getCompanyMenus(companyId: number): Promise<any[]> {
    return this.request(`/api/admin/companies/${companyId}/menus`);
  }

  async createMenu(companyId: number, data: {
    name: string;
    isActive?: boolean;
    config?: any;
  }): Promise<any> {
    return this.request(`/api/admin/companies/${companyId}/menus`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMenu(menuId: number, data: any): Promise<any> {
    return this.request(`/api/admin/menus/${menuId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteMenu(menuId: number): Promise<void> {
    return this.request(`/api/admin/menus/${menuId}`, {
      method: 'DELETE',
    });
  }

  async generateMenuQR(menuId: number, format: 'png' | 'svg' = 'png'): Promise<any> {
    return this.request(`/api/admin/menus/${menuId}/qr?format=${format}`);
  }

  async downloadMenuQR(menuId: number, format: 'png' | 'svg' = 'png'): Promise<Blob> {
    const response = await fetch(
      `${API_BASE}/api/admin/menus/${menuId}/qr/download?format=${format}`,
      { headers: this.getHeaders() }
    );

    if (!response.ok) {
      throw new Error('Failed to download QR code');
    }

    return response.blob();
  }

  async getMenuAnalytics(menuId: number, days: number = 30): Promise<any> {
    return this.request(`/api/admin/menus/${menuId}/analytics?days=${days}`);
  }

  async getPopularItems(menuId: number, limit: number = 10): Promise<any[]> {
    return this.request(`/api/admin/menus/${menuId}/popular-items?limit=${limit}`);
  }

  // ==================== MENU CATEGORIES ====================

  async getMenuCategories(menuId: number): Promise<any[]> {
    return this.request(`/api/admin/menus/${menuId}/categories`);
  }

  async createCategory(menuId: number, data: any): Promise<any> {
    return this.request(`/api/admin/menus/${menuId}/categories`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(categoryId: number, data: any): Promise<any> {
    return this.request(`/api/admin/categories/${categoryId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(categoryId: number): Promise<void> {
    return this.request(`/api/admin/categories/${categoryId}`, {
      method: 'DELETE',
    });
  }

  // ==================== MENU ITEMS ====================

  async getCategoryItems(categoryId: number): Promise<any[]> {
    return this.request(`/api/admin/categories/${categoryId}/items`);
  }

  async getMenuItems(menuId: number): Promise<any[]> {
    return this.request(`/api/admin/menus/${menuId}/items`);
  }

  async createItem(categoryId: number, data: any): Promise<any> {
    return this.request(`/api/admin/categories/${categoryId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateItem(itemId: number, data: any): Promise<any> {
    return this.request(`/api/admin/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteItem(itemId: number): Promise<void> {
    return this.request(`/api/admin/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  async bulkImportMenu(menuId: number, data: any): Promise<any> {
    return this.request(`/api/admin/menus/${menuId}/bulk-import`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const adminAPI = new AdminAPIClient();
